import { uploadFile, getDownloadUrl, getProjectMetadata } from '../utils/r2Storage';
import { InspectionRecord } from '../types';

const DB_PROJECT_ID = 'SYSTEM_DATA';
const DB_FILE_NAME = 'inspections_db.json';
const DB_CONTENT_TYPE = 'application/json';

// Helper: Get full DB content
const getDB = async (): Promise<InspectionRecord[]> => {
    try {
        // Try to get metadata using the new "getProjectMetadata" logic (which acts as a GET for JSON)
        // Or simply fetch the file if it exists.
        // Since getProjectMetadata looks for `projectId/metadata.json`, and we want `SYSTEM_DATA/inspections_db.json`,
        // let's just use the underlying fetch logic or construct the URL manually if public, 
        // BUT `getDownloadUrl` needs a specific key.

        // Strategy: 
        // 1. Get Signed URL for download
        // 2. Fetch content
        const key = `${DB_PROJECT_ID}/${DB_FILE_NAME}`;
        try {
            const url = await getDownloadUrl(key);
            const resp = await fetch(url);
            if (!resp.ok) {
                // If 404, return empty array (first time)
                if (resp.status === 404) return [];
                throw new Error('Failed to fetch DB');
            }
            const data = await resp.json();
            return Array.isArray(data) ? data : [];
        } catch (e: any) {
            // Likely file doesn't exist yet
            console.warn('[InspectionService] DB not found, initializing empty.', e);
            return [];
        }
    } catch (error) {
        console.error('[InspectionService] Error loading DB:', error);
        return [];
    }
};

// Helper: Save full DB content
const saveDB = async (records: InspectionRecord[]): Promise<void> => {
    const jsonString = JSON.stringify(records, null, 2);
    const blob = new Blob([jsonString], { type: DB_CONTENT_TYPE });

    // Upload to R2
    await uploadFile(DB_PROJECT_ID, DB_FILE_NAME, blob, DB_CONTENT_TYPE);
};

export const inspectionService = {
    // List all inspections
    getAll: async (): Promise<InspectionRecord[]> => {
        return await getDB();
    },

    // Save a new inspection
    save: async (record: InspectionRecord): Promise<void> => {
        const db = await getDB();

        // Check if exists (update if so)
        const index = db.findIndex(r => r.id === record.id);
        if (index >= 0) {
            db[index] = { ...record, updatedAt: new Date().toISOString() };
        } else {
            db.push({ ...record, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
        }

        await saveDB(db);
    },

    // Delete an inspection
    delete: async (id: string): Promise<void> => {
        const db = await getDB();
        const filtered = db.filter(r => r.id !== id);
        if (filtered.length !== db.length) {
            await saveDB(filtered);
        }
    },

    // Get stats
    getStats: async () => {
        const db = await getDB();
        return {
            total: db.length,
            critical: db.filter(r => r.severity >= 4).length,
            detected: db.filter(r => r.status === 'detected').length,
            verified: db.filter(r => r.status === 'verified').length
        };
    }
};
